import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { DateRange } from '@cyberlz/react-date-range';
import { es } from 'date-fns/locale/es';
import { parseISO } from 'date-fns';

function formatDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-ES');
}

const isMobile = () => {
  return window.innerWidth <= 768;
};

const DateRangeSelector = ({
  startDate,
  endDate,
  onChange,
  label = '',
  error = false,
  helperText = '',
  minDate,
  maxDate,
  disabled = false,
  isOneWay = false,
  months = 2,
  departureDate,
  disabledDates = [],
  startDateLabel = 'Ida',
  endDateLabel = 'Vuelta',
}) => {
  const [open, setOpen] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [calendarTop, setCalendarTop] = useState(48);
  const [calendarLeft, setCalendarLeft] = useState(0);
  const ref = useRef(null);
  const popupRef = useRef(null);
  const isIntermediateViewport = viewportWidth > 768 && viewportWidth <= 1024;
  const shouldUseFluidWidth = true;

  useEffect(() => {
    const checkMobile = () => {
      setViewportWidth(window.innerWidth);
      setIsMobileDevice(isMobile());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (isMobileDevice && open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isMobileDevice, open]);

  useLayoutEffect(() => {
    if (!open || isMobileDevice) return undefined;

    // position:fixed makes the popup escape the field's positioning context, so both
    // axes have to be derived from the field's own rect — the CSS `left: 50%` this used
    // to rely on centered the popup on the viewport instead of the field once it stopped
    // being position:absolute.
    const updateCalendarPosition = () => {
      const inputRect = ref.current?.getBoundingClientRect();
      if (!inputRect) {
        setCalendarTop(48);
        setCalendarLeft(0);
        return;
      }

      const popupWidth = popupRef.current?.getBoundingClientRect().width ?? inputRect.width;
      const idealLeft = inputRect.left + inputRect.width / 2 - popupWidth / 2;
      const maxLeft = Math.max(window.innerWidth - popupWidth - 16, 16);

      setCalendarTop(inputRect.bottom + 8);
      setCalendarLeft(Math.min(Math.max(idealLeft, 16), maxLeft));
    };

    updateCalendarPosition();
    window.addEventListener('scroll', updateCalendarPosition, true);
    window.addEventListener('resize', updateCalendarPosition);

    return () => {
      window.removeEventListener('scroll', updateCalendarPosition, true);
      window.removeEventListener('resize', updateCalendarPosition);
    };
  }, [isMobileDevice, open, isIntermediateViewport]);

  const handleDateChange = (ranges) => {
    const { startDate: newStart, endDate: newEnd } = ranges.selection;
    if (isOneWay) {
      onChange({ startDate: newStart, endDate: null });
      setOpen(false);
    } else {
      if (newStart && !newEnd) {
        onChange({ startDate: newStart, endDate: newStart });
      } else {
        onChange({ startDate: newStart, endDate: newEnd });
        if (newEnd && newEnd !== newStart) {
          setOpen(false);
        }
      }
    }
  };

  const minDateFromDeparture = departureDate ? parseISO(departureDate) : minDate;

  useEffect(() => {
    if (departureDate && !startDate && !isOneWay) {
      const departureDateObj = parseISO(departureDate);
      onChange({ startDate: departureDateObj, endDate: departureDateObj });
    }
  }, [departureDate, startDate, isOneWay, onChange]);

  const getCalendarStyle = () => {
    if (isMobileDevice) {
      const horizontalSpacing = viewportWidth >= 600 ? 120 : 16;

      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        width: `calc(100% - ${horizontalSpacing}px)`,
        maxHeight: '85vh',
        overflow: 'auto',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      };
    } else {
      return {
        position: 'fixed',
        zIndex: 1000,
        top: calendarTop,
        left: calendarLeft,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        backgroundColor: '#fff',
        width: isIntermediateViewport ? 'calc(100vw - 120px)' : 'min(760px, calc(100vw - 32px))',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 96px)',
        overflow: 'auto',
      };
    }
  };

  const renderMobileOverlay = () => {
    if (!isMobileDevice || !open) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 999,
        }}
        onClick={() => setOpen(false)}
      />
    );
  };

  return (
    <>
      {renderMobileOverlay()}
      <div ref={ref} style={{ position: 'relative', width: '100%' }}>
        {label && (
          <label
            style={{
              fontSize: 12,
              color: '#4f4f4f',
              display: 'block',
              marginBottom: 4,
            }}
          >
            {label}
          </label>
        )}
        <div
          className={`form__field date-picker ${error ? 'dates-error' : ''}`}
          onClick={() => !disabled && setOpen(!open)}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            height: 48,
            lineHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: error ? '1px solid rgb(207, 24, 0)' : '1px solid #ccc',
            borderRadius: 4,
            padding: '0 12px',
            background: disabled ? '#f5f5f5' : '#fff',
            gap: 8,
            flexDirection: 'row',
          }}
        >
          {isOneWay ? (
            <span
              style={{
                color: !startDate ? '#aaa' : '#222',
                whiteSpace: 'nowrap',
                fontSize: 16,
                display: 'inline-block',
              }}
            >
              {startDate ? formatDate(startDate) : startDateLabel}
            </span>
          ) : (
            <>
              <span
                style={{
                  color: !startDate ? '#aaa' : '#222',
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                {startDate ? formatDate(startDate) : startDateLabel}
              </span>
              <span style={{ color: '#aaa', fontSize: 18, display: 'inline-block' }}>→</span>
              <span
                style={{
                  color: !endDate ? '#aaa' : '#222',
                  whiteSpace: 'nowrap',
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                {endDate ? formatDate(endDate) : endDateLabel}
              </span>
            </>
          )}
        </div>
        {helperText && error && (
          <div
            style={{
              alignItems: 'center',
              color: 'rgb(207, 24, 0)',
              display: 'flex',
              fontSize: 11,
              gap: 4,
              justifyContent: 'flex-end',
              lineHeight: 1.2,
              marginTop: 2,
            }}
          >
            <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
            <span>{helperText}</span>
          </div>
        )}
        {open && (
          <div ref={popupRef} style={getCalendarStyle()}>
            {isMobileDevice && (
              <div
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '500' }}>
                  Seleccionar fechas
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div
              style={{
                overflow: 'visible',
                width: '100%',
                maxWidth: '100%',
                ...(isMobileDevice && {
                  padding: '0 4px',
                }),
              }}
            >
              <DateRange
                direction={isMobileDevice ? 'vertical' : 'horizontal'}
                disabledDates={disabledDates}
                layout={isMobileDevice ? 'auto' : 'reference'}
                locale={es}
                maxDate={maxDate}
                minDate={minDateFromDeparture}
                mobileBreakpoint={768}
                months={months || 2}
                onChange={handleDateChange}
                rangeColors={['#1976d2']}
                ranges={[
                  {
                    startDate: startDate || new Date(),
                    endDate: isOneWay
                      ? startDate || new Date()
                      : endDate || startDate || new Date(),
                    key: 'selection',
                  },
                ]}
                retainEndDateOnFirstSelection={false}
                showDateDisplay={false}
                showMonthAndYearPickers={false}
                widthMode={shouldUseFluidWidth ? 'fluid' : 'content'}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DateRangeSelector;
